<script lang="ts">
  // BJL-CHECK: Card (used), Badge (used, PLANNED), Button (used), Alert, Collapsible. The
  // card and its controls are bjl; the accent stripe stays a border on the wrapper because
  // `alertColor` computes it per alert (severity + planned/live) rather than picking one of
  // Alert's five fixed tones, and "Show more" is a 3-line CSS clamp so the text stays in the
  // DOM for find-in-page. See artifacts/deviations/AlertsPage.md.
  import { Card } from '@byjackli-labs/ui/Card'
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Button } from '@byjackli-labs/ui/Button'
  import { Colors } from '../../ui/colors'
  import { Type } from '../../ui/theme'
  import { alertColor, alertLabel, relativeStart } from '../../ui/alerts'
  import type { ServiceAlert } from '../../store/transit'
  import RouteBullet from './RouteBullet.svelte'
  import { alertTextParts } from './lines'

  interface Props {
    alert: ServiceAlert
    onSelect: () => void
  }

  const { alert, onSelect }: Props = $props()

  let expanded = $state(false)

  // Whether the collapsed (3-line) description actually overflows. "Show more" must only
  // appear when there's hidden text — short descriptions fit the clamp, so the button would
  // otherwise toggle its own label while revealing nothing. Measured from the DOM because
  // overflow depends on rendered width and font, which we can't know from the string alone.
  let descEl = $state<HTMLDivElement | null>(null)
  let overflows = $state(false)

  const color = $derived(alertColor(alert))
  const started = $derived(relativeStart(alert))
  const description = $derived(alert.descriptionText?.trim())
  const header = $derived(alertTextParts(alert.headerText || 'Service notice', alert.routeIds))
  const body = $derived(description ? alertTextParts(description, alert.routeIds) : [])

  // Routes are deduped: MTA repeats a line across informed entities (once per affected
  // stop), so an unfiltered list renders the same bullet a dozen times.
  const routes = $derived([...new Set(alert.routeIds)].filter(Boolean))

  // Re-measure overflow whenever the description changes (cards are recycled across alerts).
  // Only meaningful while collapsed — expanding removes the clamp, so scrollHeight would then
  // equal clientHeight and wrongly report no overflow; keep the last collapsed reading.
  $effect(() => {
    void description
    if (descEl && !expanded) {
      overflows = descEl.scrollHeight > descEl.clientHeight + 1
    }
  })
</script>

<!-- MTA's "[E][F] trains…" with each bracket drawn as the line's bullet, centred on the
     text's capitals like the icons on mta.info. The wrapper is an inline-block with
     overflow hidden so its baseline is its BOTTOM edge: an inline-flex (or plain
     inline-block) takes its baseline from the bullet's own letter, which dropped the disc
     by that letter's descent before any offset applied. From the bottom edge, raising it
     half a cap height (~0.35em) less half the disc puts the disc's centre on the caps'. -->
{#snippet richText(parts: ReturnType<typeof alertTextParts>, size: number)}
  {#each parts as part, i (i)}
    {#if 'routeId' in part}
      <span style="display: inline-block; overflow: hidden; vertical-align: calc(0.35em - {size / 2}px); margin: 0 1px;">
        <RouteBullet routeId={part.routeId} {size} />
      </span>
    {:else}{part.text}{/if}
  {/each}
{/snippet}

<!-- 6px of the alert's own severity color down the leading edge, the same weight as the bar on
     the trunk-line rows this card is reached from — so the color that summarised a group on
     the index is still the color identifying each alert inside it. -->
<div style="border-left: 6px solid {color}; border-radius: 14px; overflow: hidden;">
  <Card>
  <div style="display: flex; flex-direction: column; gap: 8px;">
  <div style="display: flex; align-items: center; gap: 8px; flex-wrap: wrap;">
    <span
      style="color: {color}; {Type.eyebrow};"
    >{alertLabel(alert)}</span>
    {#if alert.planned}
      <Badge variant="outline" size="xs" register="mono" tone={Colors.textMuted}>PLANNED</Badge>
    {/if}
    {#if started}
      <span style="color: {Colors.textMuted}; {Type.caption};">· {started}</span>
    {/if}
  </div>

  {#if routes.length > 0}
    <div style="display: flex; gap: 5px; flex-wrap: wrap;">
      {#each routes as r (r)}
        <RouteBullet routeId={r} />
      {/each}
    </div>
  {/if}

  <div style="color: {Colors.text}; {Type.bodyLg}; font-weight: var(--bjl-font-weight-semibold, 600); line-height: 1.4;">
    {@render richText(header, 20)}
  </div>

  {#if description}
    <div>
      <!-- Collapsed alerts show three lines; MTA descriptions run to several paragraphs
           and would otherwise push every other alert off the screen. -->
      <div
        bind:this={descEl}
        style="
          color: {Colors.textSecondary}; {Type.body};
          display: {expanded ? 'block' : '-webkit-box'};
          -webkit-line-clamp: {expanded ? 'unset' : '3'};
          -webkit-box-orient: vertical;
          overflow: hidden;
        "
      >{@render richText(body, 18)}</div>
      {#if overflows}
        <Button
          variant="text"
          size="sm"
          onclick={() => { expanded = !expanded }}
          aria-expanded={expanded}
          style="padding-left: 0; padding-right: 0;"
        >{expanded ? 'Show less' : 'Show more'}</Button>
      {/if}
    </div>
  {/if}

  {#if routes.length > 0}
    <Button variant="secondary" size="sm" onclick={onSelect} style="align-self: flex-start;">
      Show {routes.length === 1 ? 'this line' : 'these lines'} on map
    </Button>
  {/if}
  </div>
  </Card>
</div>
